/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Dialog from '@comp/Dialog/Dialog';
import { useIsMobile } from '@stores/AppCommonData';
import { Spacings, Fonts, mobile } from '../../../../../styleConstants';
import SpeakerDashBg from './SpeakerDashBg';
import SpeakerInfoCard from './SpeakerInfoCard';

const Wrapper = styled.div``;

const ColorBg = styled.div`
  position: absolute;
  width: 100%;
  bottom: ${Spacings.SPACING_0B};
  left: ${Spacings.SPACING_0B};
  background-color: ${({ $bgColor }) => $bgColor};
  z-index: 2;
  height: ${({ $type }) => ($type === 'big' ? '240px' : '80%')};
  border-radius: ${({ $type }) =>
    $type === 'big' ? Spacings.SPACING_5B : Spacings.SPACING_3B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    border-radius: ${({ $type }) =>
      $type === 'big' ? Spacings.SPACING_5B : '10px'};
  }
`;

const ColorRotateBg = styled(ColorBg)`
  z-index: 1;
  transform: ${({ $tiltBg }) => ($tiltBg ? 'rotate(-6deg)' : 'rotate(0deg)')};
  transition: transform 400ms ease-out;
`;

const SpeakerImage = styled.img`
  user-drag: none;
  z-index: 4;
  position: relative;
  max-width: 100%;
  width: 100%;
  border-radius: ${({ $type }) =>
    $type === 'big' ? Spacings.SPACING_5B : Spacings.SPACING_3B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    border-radius: ${({ $type }) =>
      $type === 'big' ? Spacings.SPACING_5B : '10px'};
  }
`;

const DashBgContainer = styled.div`
  position: absolute;
  bottom: ${Spacings.SPACING_0B};
  right: ${Spacings.SPACING_0B};
  z-index: 3;
  height: 240px;
  border-radius: ${({ $type }) =>
    $type === 'big' ? Spacings.SPACING_5B : Spacings.SPACING_3B};
  overflow: hidden;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    border-radius: ${({ $type }) =>
      $type === 'big' ? Spacings.SPACING_5B : '10px'};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  cursor: pointer;
  max-width: 320px;
  height: ${({ $type }) => ($type === 'big' ? '300px' : '200px')};
  margin: auto;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    height: ${({ $type }) => ($type === 'big' ? '320px' : '150px')};
  }
`;

const SpeakerName = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${({ $type }) => ($type === 'big' ? Fonts.H2 : Fonts.H3)};
  line-height: ${({ $type }) =>
    $type === 'big' ? Spacings.SPACING_10B : '30px'};
  padding-top: ${Spacings.SPACING_6B};
  color: ${({ $color }) => $color};
  text-align: center;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    font-size: ${({ $type }) => ($type === 'big' ? Fonts.H2 : Fonts.H4)};
    line-height: ${({ $type }) =>
      $type === 'big' ? Spacings.SPACING_10B : '25px'};
  }
`;

const SpeakerInfo = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${({ $type }) => ($type === 'big' ? Fonts.H4 : '18px')};
  line-height: ${({ $type }) => ($type === 'big' ? '25px' : '22px')};
  padding-top: ${Spacings.SPACING_6};
  color: ${({ $color }) => $color};
  text-align: center;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    line-height: ${({ $type }) =>
      $type === 'big' ? Fonts.HEADER_LINE_HEIGHT : Fonts.PARA_LINE_HEIGHT};
    font-size: ${({ $type }) => ($type === 'big' ? Fonts.H4 : Fonts.H6)};
  }
`;

const Speaker = ({ type, author }) => {
  const isMobileDevice = useIsMobile();
  const { name, subText, bgColor, textColor, svgColor, username, avatar } =
    author;
  const [tiltBg, setTiltBg] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const onMouseEnter = () => {
    setTiltBg(true);
  };
  const onMouseLeave = () => {
    setTiltBg(false);
  };
  const onClick = () => {
    setShowInfo(true);
  };
  return (
    <Wrapper>
      <ImageContainer
        onMouseOver={isMobileDevice ? undefined : onMouseEnter}
        onMouseLeave={isMobileDevice ? undefined : onMouseLeave}
        onClick={onClick}
        $type={type}
      >
        {!isMobileDevice && (
          <ColorRotateBg $type={type} $bgColor={textColor} $tiltBg={tiltBg} />
        )}
        <ColorBg $type={type} $bgColor={bgColor} />
        {type === 'big' && (
          <DashBgContainer $type={type}>
            <SpeakerDashBg color={svgColor} />
          </DashBgContainer>
        )}
        <SpeakerImage
          src={
            avatar ||
            `https://quark.uacdn.net/summit/${encodeURIComponent(username)}.png`
          }
          $type={type}
        />
      </ImageContainer>
      <SpeakerName $type={type} $color={textColor}>
        {name}
      </SpeakerName>
      <SpeakerInfo $type={type} $color={textColor}>
        {subText}
      </SpeakerInfo>
      <Dialog
        onClose={() => setShowInfo(false)}
        dialogState={showInfo}
        center
        borderRadius={Spacings.SPACING_5B}
        maxWidth="sm"
      >
        <SpeakerInfoCard author={author} />
      </Dialog>
    </Wrapper>
  );
};

Speaker.propTypes = {
  type: PropTypes.oneOf(['big', 'small']),
  author: PropTypes.objectOf(PropTypes.any).isRequired
};

Speaker.defaultProps = {
  type: 'big'
};

export default Speaker;
